import {Breadcrumb} from '@/components/atoms'
import {PageHeader, TabsComponent} from '@/components/molecules'
import {BaseDataEdit, BrandAndCategoryEdit, MediaGalleryEdit, ProductDisplayEdit} from '@/containers'
import {TaxAndSalesEdit} from '@/containers/ProductReviewEditContainer/TaxAndSales/TaxAndSalesEdit'
import {useMediaUpload} from '@/hooks/useMediaUpload'
import {updateProduct} from '@/services/ProductsService/product'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {ROUTES} from '@/utils/constants/routesConstants'
import {convertEmptyStringsToNull} from '@/utils/objectUtils'
import {useQueryClient} from '@tanstack/react-query'
import {useEffect, useMemo, useState} from 'react'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {useProductOverview} from './ProductReview/useProductOverview'

export const ProductReviewEdit = () => {
  const {id} = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()

  const {productOverviewData} = useProductOverview()
  const [formData, setFormData] = useState({})
  const existingProductMedia = useMemo(
    () => (Array.isArray(productOverviewData?.data?.media) ? productOverviewData.data.media : []),
    [productOverviewData?.data?.media]
  )

  const {mediaSlots, isUploading, updateMediaSlot, clearMediaSlot, uploadMedias} = useMediaUpload(
    id,
    existingProductMedia
  )

  const location = useLocation()
  const state = location?.state

  useEffect(() => {
    if (productOverviewData?.data) {
      setFormData(productOverviewData.data)
    }
  }, [productOverviewData])

  const handleFormChange = updatedData => {
    setFormData(prevData => {
      const newData = {...prevData, ...updatedData}
      return newData
    })
  }

  const handleSaveProduct = async dataToSave => {
    try {
      const hasMediaFilesToUpload = mediaSlots.some(slot => slot.file && slot.metadata)
      const dataToSubmit = dataToSave || formData

      const sanitizedData = convertEmptyStringsToNull(dataToSubmit)

      // If media is an empty array, remove it from the payload
      if (Array.isArray(sanitizedData?.media) && sanitizedData.media.length === 0) {
        delete sanitizedData.media
      }

      const updateProductRequest = updateProduct(sanitizedData)
      const uploadMediaRequest = hasMediaFilesToUpload ? uploadMedias() : Promise.resolve(true)

      const [response, mediaUploadSucceeded] = await Promise.all([updateProductRequest, uploadMediaRequest])

      if (!mediaUploadSucceeded) return response

      const targetId = id || response?.id || response?.productId
      if (targetId) {
        // Invalidate both the product list and the individual product queries
        await queryClient.invalidateQueries(['PRODUCT_LIST'])
        await queryClient.invalidateQueries(['PRODUCT', targetId])

        navigate(ROUTES.PRODUCTS_LIST)
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Product Successfully Updated',
            type: 'success',
          })
        )
      } else {
        console.warn('Could not determine ID for redirection after save.')
      }

      return response
    } catch (error) {
      dispatch(
        setToastNotifications({
          show: true,
          message: `Error saving product: ${error}`,
          type: 'error',
        })
      )
      throw error
    }
  }

  if (!formData || Object.keys(formData).length === 0) {
    return <div>Loading ...</div>
  }

  const productName = formData.name || formData.productName || formData.baseData?.name || 'Loading...'
  const tabList = [
    {
      id: 'Base Data',
      tabLabel: 'Base Data',
      tabComponent: <BaseDataEdit formData={formData} onDataChange={handleFormChange} onPublish={handleSaveProduct} />,
    },
    {
      id: 'Media',
      tabLabel: 'Media',
      tabComponent: (
        <MediaGalleryEdit
          mediaSlots={mediaSlots}
          isUploading={isUploading}
          updateMediaSlot={updateMediaSlot}
          clearMediaSlot={clearMediaSlot}
          handleSave={handleSaveProduct}
        />
      ),
    },
    {
      id: 'Display',
      tabLabel: 'Display',
      tabComponent: (
        <ProductDisplayEdit formData={formData} setFormData={handleFormChange} handleSave={handleSaveProduct} />
      ),
    },
    {
      id: 'Brand & Category',
      tabLabel: 'Brand & Category',
      tabComponent: (
        <BrandAndCategoryEdit formData={formData} setFormData={handleFormChange} handleSave={handleSaveProduct} />
      ),
    },
    {
      id: 'Tax & Sales ',
      tabLabel: 'Tax & Sales ',
      tabComponent: (
        <TaxAndSalesEdit formData={formData} setFormData={handleFormChange} handleSave={handleSaveProduct} />
      ),
    },
  ]

  const breadcrumbs = [
    {href: '/', label: ''},
    {href: ROUTES.PRODUCTS_LIST, label: 'Product view'},
    {href: '', label: productName},
  ]

  return (
    <>
      <Breadcrumb uiSchema={{isDark: true, className: 'mb-5', breadcrumbs}} id="productOverviewBreadcrumb" />
      <div className="mb-4 flex items-center justify-between">
        <PageHeader title={productName} />
      </div>
      <TabsComponent
        isDarkTab={false}
        isBorderVisible={false}
        tabList={tabList}
        defaultTabIndex={state?.selectedTabNumb ?? 0}
        removeBG={true}
      />
    </>
  )
}

export default ProductReviewEdit
